import React, { useEffect, useRef, useState } from 'react';
import styles from './sort-modal.module.css';
import { useFetchUsers } from '../../../actions';
import { Button } from '../../button';
import { SortOrder } from '../../../types';
import { SortOrderEnum } from '../../../constants';
import { useTranslation } from 'react-i18next';

type SortModalProps = {
  columns: { header: string; accessor: string }[];
  onClose: () => void;
};

export const SortModal: React.FC<SortModalProps> = ({ columns, onClose }) => {
  const { t } = useTranslation();
  const savedColumn = localStorage.getItem('selectedColumn') || '';
  const savedDirection = (localStorage.getItem('sortDirection') as SortOrder) || SortOrderEnum.ASC;
  const search = localStorage.getItem('search') as string;
  const [selectedColumn, setSelectedColumn] = useState<string>(savedColumn || '');
  const [sortDirection, setSortDirection] = useState<SortOrder>(
    savedDirection || SortOrderEnum.ASC,
  );
  const fetchUsers = useFetchUsers(search, selectedColumn, sortDirection);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleApply = () => {
    if (selectedColumn) {
      localStorage.setItem('selectedColumn', selectedColumn);
      localStorage.setItem('sortDirection', sortDirection);
      fetchUsers();
    }
    onClose();
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      className={styles.modal}
      ref={containerRef}
    >
      <div className={styles.header}>{t('sort')}</div>
      <div className={styles.body}>
        {columns.map((column) => (
          <label
            key={column.header}
            className={styles.option}
          >
            <input
              type="radio"
              name="sortColumn"
              value={column.accessor}
              checked={selectedColumn === column.accessor}
              onChange={(e) => setSelectedColumn(e.target.value)}
            />
            {column.header}
          </label>
        ))}
        <div className={styles.sortDirection}>
          <label>
            <input
              className={styles.sortInput}
              type="radio"
              name="sortDirection"
              value={SortOrderEnum.ASC}
              checked={sortDirection === SortOrderEnum.ASC}
              onChange={() => setSortDirection(SortOrderEnum.ASC)}
            />
            <i className="fas fa-sort-amount-up" />
            <span className={styles.sortDirectionLabel}>{t('ascending')}</span>
          </label>
          <label>
            <input
              className={styles.sortInput}
              type="radio"
              name="sortDirection"
              value={SortOrderEnum.DESC}
              checked={sortDirection === SortOrderEnum.DESC}
              onChange={() => setSortDirection(SortOrderEnum.DESC)}
            />
            <i className="fas fa-sort-amount-down" />{' '}
            <span className={styles.sortDirectionLabel}>{t('descending')}</span>
          </label>
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.cancelButton}
          onClick={onClose}
          variant="danger"
        >
          {t('cancel')}
        </Button>
        <Button
          className={styles.applyButton}
          onClick={handleApply}
          disabled={!selectedColumn}
        >
          {t('applySorting')}
        </Button>
      </div>
    </div>
  );
};
